/**
 * Romanian catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Romanian at a mathematician's register. **Warrant → «Justificare»**
 * (epistemic justification; Toulmin's «temei/garant» is the flagged alternative). Refutation
 * follows Popper's «infirmare» → «Infirmat». W/M grades stay Latin (`::numbering-system/latn`).
 * CLDR cardinal categories are one / few / other (Romanian uses `few` for 2–19 and the «de»
 * form for `other` ≥ 20), so `frameActive` declares exactly those (plus an explicit `=0`).
 * Per INV-STABLE-ID a wrong translation is a presentation concern only — verdicts stay fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const RO_CATALOG = {
    locale: 'ro',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Investigație',
        [P2.sectionClaims]: 'Afirmații',
        [P2.sectionIntegrity]: 'Integritate',
        [P2.axisWarrantLabel]: 'Justificare',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empiric',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definiție} interp {Interpretare} testable {Testabil} tested {Testat} refuted {Infirmat} program {Program de cercetare} philos {Filosofic} other {Necunoscut}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Nivel de forță {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Explorabil',
        [P2.stateRefuted]: 'Infirmat',
        [P2.stateOutside]: 'În afara domeniului',
        [P2.verdictConflict]: 'În conflict cu {constraints}',
        [P2.verdictOutside]: 'Necesită {missing}',
        [P2.lossRegisterPlain]: 'Simplificat în limbaj comun — o parte din precizie este omisă.',
        [P2.lossRegisterKid]: 'Simplificat pentru cititorii tineri — multe detalii sunt omise.',
        [P2.lossProfileDetail]: 'Profilul complet Justificare / Empiric / Modal este rezumat aici.',
        [P2.lossConflictDetail]: 'Detaliile conflictului sunt prescurtate în această vizualizare.',
        [P2.laneVerified]: 'Verificat',
        [P2.laneUnverified]: 'Neverificat',
        [P2.laneConflict]: 'Conflict dovedit',
        [P2.constraintLogic]: 'Logică',
        [P2.constraintLightBends]: 'Lumina se curbează într-un câmp gravitațional',
        [P2.constraintParallelPostulate]: 'Postulatul paralelelor',
        [P2.claimScalarGravity]: 'Teoria scalară a gravitației',
        [P2.claimNonEuclidean]: 'Geometrie neeuclidiană',
        [P2.claimSubstrateSr]: 'Interpretarea prin substrat a relativității restrânse',
        [P2.edgeExtObservation]: 'Observație externă',
        [P2.edgeResourceSample]: 'Resursă de calcul partajată',
        [P2.edgeHeuristicSample]: 'Îndrumare euristică',
        [P2.edgeSharedSample]: 'Fundament comun',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Restricții',
        [P2.sectionEdges]: 'Muchii',
        [P2.sectionFrame]: 'Cadru',
        [P2.sectionExposure]: 'Expunere',
        [P2.statusAdoptedFormal]: 'Adoptat (formal)',
        [P2.statusAdoptedInterpretive]: 'Adoptat (interpretativ)',
        [P2.statusSuspended]: 'Suspendat',
        [P2.edgeEffectConstraint]: 'Compune o restricție (poate infirma)',
        [P2.edgeEffectResource]: 'Resursă — fără efect asupra verdictelor',
        [P2.edgeEffectHeuristic]: 'Euristică — fără efect asupra verdictelor',
        [P2.edgeEffectShared]: 'Fundament comun — fără efect asupra verdictelor',
        [P2.frameActive]: '{count, plural, =0 {Nicio restricție activă} one {# restricție activă} few {# restricții active} other {# de restricții active}}',
        [P2.exposureNone]: 'Fără dependențe interpretative',
        [P2.exposureDep]: 'Depinde de {id}',
        [P2.laneTrackMachine]: 'Mașină',
        [P2.laneTrackExpert]: 'Expert',
        [P2.laneTrackCommunity]: 'Comunitate',
        [P2.expertAudited]: 'Auditat',
        [P2.expertInProgress]: 'În curs',
        [P2.expertNotReviewed]: 'Neexaminat',
        [P2.communityFlags]: 'Există semnalări',
        [P2.communityNone]: 'Fără semnalări',
        [P2.controlChallenge]: 'Contestă',
        [P2.controlActivate]: 'Activează',
        [P2.controlMakeInterpretive]: 'Fă interpretativ',
        [P2.controlImportEdge]: 'Importă muchie',
        [P2.controlPromote]: 'Promovează',
        [P2.controlUndo]: 'Anulează',
        [P2.controlRedo]: 'Refă',
        [P2.controlRegister]: 'Înregistrează',
        [P2.promoteRejected]: 'Promovare respinsă',
        [P2.promoteAccepted]: 'Promovare acceptată',
        [P2.promoteReasonRefutationTerminal]: 'O demonstrație nu poate vindeca o infirmare.',
        [P2.promoteReasonKeystone]: 'O promovare-cheie necesită sprijin empiric.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Tipul empiric nu este fixat.',
        [P2.promoteReasonModalNotFixed]: 'Gradul modal nu este fixat.',
        [P2.promoteReasonWarrantNotImproved]: 'Gradul de justificare nu s-a îmbunătățit.',
        [P2.evidenceComputation]: "{engine} a calculat {op} = {result} sub {config}",
        [P2.lossNumericRounding]: "Rezultat rotunjit la {dp} zecimale",
    },
};
//# sourceMappingURL=ro.js.map